import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../types';
import api from '../services/api';
import { PRAV_SOLUTIONS_HOME_URL } from '../constants';

function ChevronRight({ className = 'w-[18px] h-[18px] text-slate-400' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function IconGavel({ className = 'w-5 h-5 text-blue-600' }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M2 21h10v-2H2v2zm14.09-8.45l1.41-1.41-6.36-6.36-2.12 2.12 6.36 6.36zm.71-7.07l1.41 1.41L16.24 4.5l-1.41-1.41-4.24 4.24 1.41 1.41 4.24-4.24zM11.32 6.32l6.36 6.36 2.83-2.83-6.36-6.36-2.83 2.83z" />
    </svg>
  );
}

function IconLaptop({ className = 'w-5 h-5 text-blue-600' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <rect x="3" y="4" width="18" height="12" rx="2" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M2 18h20" />
    </svg>
  );
}

function IconShare({ className = 'w-[18px] h-[18px] text-blue-600' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <circle cx="18" cy="5" r="2.5" strokeWidth={2} />
      <circle cx="6" cy="12" r="2.5" strokeWidth={2} />
      <circle cx="18" cy="19" r="2.5" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M8.5 10.5L15.5 6.5M8.5 13.5l7 4" />
    </svg>
  );
}

function IconHelp({ className = 'w-5 h-5 text-emerald-600' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="9" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M9.5 9.5a2.5 2.5 0 115 0c0 2-2.5 2-2.5 4M12 17h.01" />
    </svg>
  );
}

function IconLogout({ className = 'w-5 h-5 text-red-500' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function IconAccount({ className = 'w-6 h-6 text-blue-600' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function getPlanDisplayName(user: User | null) {
  if (!user) return 'Standard Free';
  switch (user.plan) {
    case 'PRO':
      return 'Pro (90 Days)';
    case 'PREMIUM':
      return 'Premium (30 Days)';
    default:
      return 'Standard Free';
  }
}

type Props = {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onOpenSubscription: () => void;
  onSignOut: () => void | Promise<void>;
};

export function MainAppDrawer({ open, onClose, user, onOpenSubscription, onSignOut }: Props) {
  const navigate = useNavigate();
  const [refEmails, setRefEmails] = useState('');
  const [pravLogoVisible, setPravLogoVisible] = useState(true);
  const [sendingInvites, setSendingInvites] = useState(false);

  if (!open) return null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleSendReference = async () => {
    const emailList = refEmails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && e.includes('@'));
    if (emailList.length === 0) {
      window.alert('Please enter at least one valid email address.');
      return;
    }
    setSendingInvites(true);
    try {
      type RefResp = {
        sent: number;
        invalid: string[];
        failed: Array<{ email: string; error: string }>;
      };
      const res = await api.post<RefResp>('/api/users/me/reference-invites', { emails: emailList });
      if (!res.success) {
        window.alert(res.error || 'Could not send invites.');
        return;
      }
      const { sent = 0, invalid = [], failed = [] } = res.data || {};
      let message = `Successfully sent ${sent} email(s) with the BidMaster introduction.`;
      if (invalid.length > 0) {
        message += `\n\nSkipped (not valid): ${invalid.join(', ')}`;
      }
      if (failed.length > 0) {
        message += `\n\nCould not deliver to: ${failed.map((f) => f.email).join(', ')}`;
      }
      window.alert(message);
      setRefEmails('');
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not send invites. Try again.';
      window.alert(msg);
    } finally {
      setSendingInvites(false);
    }
  };

  const handleHelp = () => {
    go('/help-support');
  };

  const handleWebLogin = () => {
    onClose();
    window.alert("You're already using BidMaster on the web.");
  };

  const plan = user?.plan ?? 'FREE';
  const planCardClass = plan === 'PRO' ? 'bg-[#312e81]' : 'bg-[#1e3a5f]';

  return (
    <div className="fixed inset-0 z-[150] flex">
      <aside className="flex h-full w-[min(100%,300px)] max-w-[300px] flex-col bg-white shadow-xl">
        {/* Blue header — matches mobile HomeDrawer */}
        <div className="flex shrink-0 items-center justify-between bg-blue-600 px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white">
              <img src="/bidmaster-icon.png" alt="" width={32} height={32} className="h-8 w-8 object-contain" />
            </div>
            <span className="text-xl font-black tracking-tight text-white">BidMaster</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white transition-opacity hover:bg-white/30"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {/* Profile row */}
          <button
            type="button"
            onClick={() => go('/profile')}
            className="flex w-full items-center border-b border-slate-100 bg-slate-50 px-5 py-5 text-left transition-colors hover:bg-slate-100 active:bg-slate-100"
          >
            <div className="mr-3.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border border-slate-200 bg-white">
              <IconAccount />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black text-slate-800">{user?.name || 'User'}</p>
              <p className="truncate text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {user?.designation?.trim() || '—'}
              </p>
            </div>
            <ChevronRight />
          </button>

          <button
            type="button"
            onClick={() => go('/history')}
            className="flex w-full items-center px-5 py-3.5 text-left transition-colors hover:bg-blue-50 active:bg-blue-50"
          >
            <span className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100">
              <IconGavel />
            </span>
            <span className="text-sm font-bold text-slate-700">Your Bid</span>
          </button>

          <button
            type="button"
            onClick={handleWebLogin}
            className="flex w-full items-center px-5 py-3.5 text-left transition-colors hover:bg-blue-50 active:bg-blue-50"
          >
            <span className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100">
              <IconLaptop />
            </span>
            <span className="text-sm font-bold text-slate-700">Web Login</span>
          </button>

          {/* Reference invites */}
          <div className="border-t border-slate-100 px-5 pb-5 pt-4">
            <div className="mb-3 flex items-center">
              <span className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                <IconShare />
              </span>
              <span className="text-[10px] font-black tracking-widest text-slate-400">REFERENCE INVITES</span>
            </div>
            <textarea
              value={refEmails}
              onChange={(e) => setRefEmails(e.target.value)}
              placeholder="Invite emails (comma separated)"
              rows={2}
              disabled={sendingInvites}
              className="min-h-[64px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-[13px] text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => void handleSendReference()}
              disabled={sendingInvites}
              className="mt-3 w-full rounded-xl bg-blue-600 py-3.5 text-[11px] font-black uppercase tracking-widest text-white shadow-md shadow-blue-600/30 transition-opacity hover:opacity-95 active:opacity-90 disabled:opacity-60"
            >
              {sendingInvites ? 'SENDING…' : 'SEND INVITES'}
            </button>
          </div>

          {/* Help */}
          <button
            type="button"
            onClick={handleHelp}
            className="flex w-full items-center border-t border-slate-100 px-5 py-3.5 text-left transition-colors hover:bg-emerald-50 active:bg-emerald-50"
          >
            <span className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
              <IconHelp />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-700">Reach Us for Help</p>
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Get support from our team</p>
            </div>
            <ChevronRight />
          </button>

          {/* Plan card */}
          <div className="px-5 pb-2 pt-4">
            <div className={`rounded-[20px] p-4 shadow-lg ${planCardClass}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[9px] font-black tracking-widest text-white/60">PLAN DETAILS</span>
                <span className="rounded px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-slate-800 bg-amber-400">
                  {plan}
                </span>
              </div>
              <p className="mb-3 text-sm font-black text-white">{getPlanDisplayName(user)}</p>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenSubscription();
                }}
                className="w-full rounded-xl bg-white/15 py-2.5 text-center text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-white/25 active:bg-white/25"
              >
                MANAGE SUBSCRIPTION
              </button>
            </div>
          </div>

          {/* Sign out */}
          <div className="border-t border-slate-100 bg-[#fafafa] px-4 pb-2 pt-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                void onSignOut();
              }}
              className="flex w-full items-center rounded-xl px-4 py-3 text-left transition-colors hover:bg-red-50 active:bg-red-50"
            >
              <span className="mr-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100">
                <IconLogout />
              </span>
              <span className="text-sm font-bold text-red-500">Sign Out</span>
            </button>
          </div>

          {pravLogoVisible && (
            <div className="px-4 pb-6 pt-3">
              <a
                href={PRAV_SOLUTIONS_HOME_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Prav Solutions website"
                className="block w-full rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <img
                  src="/prav-solutions-logo.png"
                  alt="Prav Solutions"
                  width={268}
                  height={80}
                  className="h-20 w-full object-contain object-center opacity-95"
                  onError={() => setPravLogoVisible(false)}
                />
              </a>
            </div>
          )}
        </div>
      </aside>
      <button type="button" className="min-w-0 flex-1 bg-black/50" aria-label="Close menu" onClick={onClose} />
    </div>
  );
}
